import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChatCircleText, PaperPlaneTilt } from '@phosphor-icons/react';
import axios from 'axios';
import { API, authHeaders, formatApiError } from '../api/ticketsApi';
import { formatUtcToIst } from '../utils/datetime';
import { Surface, SectionTitle } from '../ui';
import useVisibleInterval from '../../../hooks/useVisibleInterval';

function contextPreview(ctx) {
  if (!ctx) return null;
  const desc = (ctx.description || '').trim();
  return (
    <div className="tk-chat-context">
      <div className="tk-chat-context-id">{ctx.ticket_no}</div>
      {ctx.subject ? <div className="tk-chat-context-subject">{ctx.subject}</div> : null}
      {(ctx.client_name || ctx.branch) ? (
        <div className="tk-chat-context-place">
          {[ctx.client_name, ctx.branch].filter(Boolean).join(' / ')}
        </div>
      ) : null}
      {desc ? <div className="tk-chat-context-desc">{desc}</div> : null}
    </div>
  );
}

function ChatFile({ ticketNo, file }) {
  const [opening, setOpening] = useState(false);
  const [preview, setPreview] = useState('');
  const isImage = /^image\//.test(file.content_type || '')
    || /\.(png|jpe?g|gif|webp|bmp)$/i.test(file.filename || '');

  useEffect(() => {
    if (!isImage) return undefined;
    let objectUrl = '';
    let cancelled = false;
    const token = localStorage.getItem('token');
    axios.get(`${API}/${ticketNo}/attachments/${encodeURIComponent(file.filename)}`, {
      withCredentials: true,
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(res.data);
      setPreview(objectUrl);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ticketNo, file.filename, file.content_type, isImage]);

  const open = async () => {
    setOpening(true);
    try {
      if (preview) {
        window.open(preview, '_blank', 'noopener');
        return;
      }
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/${ticketNo}/attachments/${encodeURIComponent(file.filename)}`, {
        withCredentials: true,
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
    } catch {
      setOpening(false);
      return;
    }
    setOpening(false);
  };

  return (
    <div className="tk-chat-file">
      {preview ? (
        <button type="button" className="tk-chat-file-preview" onClick={open}>
          <img src={preview} alt={file.filename} />
        </button>
      ) : null}
      <button type="button" className="tk-chat-file-btn" onClick={open} disabled={opening}>
        {opening ? 'Opening…' : `View ${file.filename}`}
      </button>
    </div>
  );
}

export default function TicketClientChat({ ticketNo, ticket, viewOnly, onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [chatClosed, setChatClosed] = useState(
    ['closed', 'cancelled'].includes((ticket?.status || '').toLowerCase()),
  );
  const scroller = useRef(null);
  const reporterEmail = ticket?.reporter?.email
    || ticket?.domain_fields?.reporter_email
    || ticket?.email
    || '';

  const load = useCallback(async ({ silent } = {}) => {
    if (!ticketNo) return;
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/${ticketNo}/chat`, authHeaders());
      setMessages(res.data.messages || []);
      const nextUnread = res.data.unread?.staff_unread || 0;
      setUnread(nextUnread);
      if (onUnreadChange) onUnreadChange(nextUnread);
      setChatClosed(!!res.data.chat_closed || ['closed', 'cancelled'].includes((res.data.status || ticket?.status || '').toLowerCase()));
      setError(null);
    } catch (err) {
      setError(formatApiError(err, 'Could not load chat'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [ticketNo, onUnreadChange, ticket?.status]);

  useEffect(() => {
    load();
  }, [load]);

  useVisibleInterval(() => load({ silent: true }), 15000);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending || viewOnly || chatClosed) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/${ticketNo}/chat`, { body }, authHeaders());
      setMessages(res.data.messages || []);
      setDraft('');
    } catch (err) {
      setError(formatApiError(err, 'Failed to send'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Surface className="tk-surface-section" style={{ marginBottom: 0 }}>
      <SectionTitle>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ChatCircleText size={15} /> Client chat
          {unread > 0 ? <span className="tk-detail-tab-count">{unread}</span> : null}
        </span>
      </SectionTitle>
      <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 10px' }}>
        One-to-one with the requester
        {reporterEmail ? ` (${reporterEmail})` : ''}.
        Your first message includes this ticket ID and description in their widget Chat tab
        (same product / client / branch mapping — no extra widget to create).
      </p>
      {loading ? (
        <p style={{ fontSize: 12, color: '#94A3B8' }}>Loading chat…</p>
      ) : error ? (
        <p style={{ fontSize: 12, color: '#B91C1C' }}>{error}</p>
      ) : (
        <div ref={scroller} className="tk-chat-scroller">
          {messages.length === 0 ? (
            <p style={{ fontSize: 12, color: '#94A3B8', margin: 0 }}>
              {chatClosed
                ? 'This ticket is closed. Chat has ended.'
                : 'No messages yet. Send a note — the client sees it beside My tickets in their support widget.'}
            </p>
          ) : messages.map((m) => (
            <div
              key={m.message_id}
              className={`tk-chat-bubble tk-chat-bubble--${m.sender_role}${m.kind === 'ticket_context' ? ' tk-chat-bubble--context' : ''}`}
            >
              {m.kind === 'ticket_context' ? contextPreview(m.ticket_context) : (
                <>
                  {m.body ? <p>{m.body}</p> : null}
                  {(m.attachments || []).map((file) => (
                    file?.filename ? (
                      <ChatFile key={file.filename} ticketNo={ticketNo} file={file} />
                    ) : null
                  ))}
                </>
              )}
              <span className="tk-chat-meta">
                {m.kind === 'ticket_context' ? 'Ticket context' : (m.sender_name || m.sender_role)}
                {m.created_at ? ` · ${formatUtcToIst(m.created_at)}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
      {viewOnly ? (
        <p style={{ fontSize: 12, color: '#94A3B8', margin: '10px 0 0' }}>Chat is read-only on a shared link.</p>
      ) : chatClosed ? (
        <div className="tk-chat-ended">
          <strong>Chat ended</strong>
          <span>This ticket is closed. Neither support nor the client can send messages until it is reopened.</span>
        </div>
      ) : (
        <form onSubmit={send} className="tk-chat-composer">
          <textarea
            className="tk-field"
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Ask the client a question about this ticket…"
          />
          <button type="submit" className="tk-btn tk-btn-primary" disabled={sending || !draft.trim()}>
            <PaperPlaneTilt size={14} /> {sending ? '…' : 'Send'}
          </button>
        </form>
      )}
    </Surface>
  );
}
